import { Button, DatePicker, DeleteIcon, Input, Select, SelectOptionType } from '@bp/ui-components';
import { FieldArray, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useQuery } from 'urql';
import {
  PimListOrganizationsDocument,
  PimListOrganizationsQuery,
  PimListOrganizationsQueryVariables,
} from '../../../client/graphql-client-defs';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { ApplicationsResponse } from '../graphql/types';

export type ApplicationLicensedOrganizations = {
  notAfter?: string | null;
  notBefore?: string | null;
  tecartContractId?: string;
  node: { uuid: string; name: string };
};

export const LicensedOrganizationsForm = () => {
  const { values, handleChange, setFieldTouched, setFieldValue } = useFormikContext<ApplicationsResponse>();
  const { t } = useTranslation();
  const organizationContext = useMemo(() => ({ additionalTypenames: ['Organization'] }), []);
  const [{ data }] = useQuery<PimListOrganizationsQuery, PimListOrganizationsQueryVariables>({
    query: PimListOrganizationsDocument,
    context: organizationContext,
  });
  const options = useCreateSelectOptions(
    data?.organizations.filter(
      (organization) =>
        !values.licensedOrganizationsConnection.edges.find((value) => value.node.uuid === organization.uuid),
    ),
    'uuid',
    'name',
  );

  return (
    <FieldArray name={'licensedOrganizationsConnection.edges'}>
      {(arrayHelpers) => {
        return (
          <>
            <Select
              isSearchable
              label={t('applications.addLicensedOrganization') as string}
              name={'organization'}
              disabled={options.length === 0}
              options={options}
              value={{ label: '', value: '' }}
              onChange={(option) => {
                const opt = option as SingleValue<SelectOptionType>;
                arrayHelpers.insert(0, {
                  notBefore: undefined,
                  notAfter: undefined,
                  node: { name: opt?.label, uuid: opt?.value },
                } as ApplicationLicensedOrganizations);
              }}
            />
            {values.licensedOrganizationsConnection.edges.map((licensedOrganization, index) => {
              return (
                <div key={licensedOrganization.node.uuid}>
                  <div className={'form-group'}>
                    <div className={'form-col'}>
                      <div className={'form-row'}>
                        <p>{licensedOrganization.node.name}</p>
                      </div>
                    </div>
                  </div>
                  <div className={'form-group'}>
                    <div className={'form-col'}>
                      <div className={'form-row'}>
                        <DatePicker
                          name={`licensedOrganizationsConnection.edges.${index}.properties.notBefore`}
                          label={t('applications.licensedOrganization.notBefore') as string}
                          onChange={async (date) => {
                            await setFieldTouched(
                              `licensedOrganizationsConnection.edges.${index}.properties.notBefore`,
                              true,
                            );
                            await setFieldValue(
                              `licensedOrganizationsConnection.edges.${index}.properties.notBefore`,
                              date,
                            );
                          }}
                          value={
                            values.licensedOrganizationsConnection.edges[index].properties?.notBefore
                              ? new Date(values.licensedOrganizationsConnection.edges[index].properties.notBefore)
                              : undefined
                          }
                          locale={'de'}
                          className={'third'}
                        />
                        <DatePicker
                          name={`licensedOrganizationsConnection.edges.${index}.properties.notAfter`}
                          label={t('applications.licensedOrganization.notAfter') as string}
                          onChange={async (date) => {
                            await setFieldTouched(
                              `licensedOrganizationsConnection.edges.${index}.properties.notAfter`,
                              true,
                            );
                            await setFieldValue(
                              `licensedOrganizationsConnection.edges.${index}.properties.notAfter`,
                              date,
                            );
                          }}
                          value={
                            values.licensedOrganizationsConnection.edges[index].properties?.notAfter
                              ? new Date(values.licensedOrganizationsConnection.edges[index].properties.notAfter)
                              : undefined
                          }
                          locale={'de'}
                          className={'third'}
                        />
                        <Input
                          type={'number'}
                          name={`licensedOrganizationsConnection.edges.${index}.properties.tecartContractId`}
                          label={'TecArt Vertrags-ID'}
                          onChange={handleChange}
                          value={values.licensedOrganizationsConnection.edges[index].properties?.tecartContractId}
                          className={'third'}
                        />
                      </div>
                    </div>
                    <div className={'form-col'}>
                      <Button icon={<DeleteIcon className={'svg-icon'} />} onClick={() => arrayHelpers.remove(index)} />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        );
      }}
    </FieldArray>
  );
};
